import React, { useState, useEffect, useRef } from 'react';
import { settingsAPI, getBillUrl } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Settings, 
  Image, 
  Save, 
  Loader2, 
  RefreshCw,
  Eye,
  Palette,
  Type,
  Upload,
  Link
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

// Use relative URLs for production compatibility - always use empty string for deployed environments
const API_URL = '';

export const SettingsPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [inputMode, setInputMode] = useState('upload'); // 'upload' or 'url'
  const [logoInputMode, setLogoInputMode] = useState('upload');
  const bannerFileRef = useRef(null);
  const logoFileRef = useRef(null);
  const [settings, setSettings] = useState({
    login_banner_url: '',
    app_name: '',
    app_tagline: '',
    primary_color: '',
    logo_url: ''
  });
  const [previewUrl, setPreviewUrl] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await settingsAPI.get();
      setSettings({
        login_banner_url: response.data.login_banner_url || '',
        app_name: response.data.app_name || '',
        app_tagline: response.data.app_tagline || '',
        primary_color: response.data.primary_color || '',
        logo_url: response.data.logo_url || ''
      });
      setPreviewUrl(response.data.login_banner_url || '');
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await settingsAPI.update(settings);
      toast.success('Settings saved successfully!');
      setPreviewUrl(settings.login_banner_url);
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to save settings';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleImageUpload = async (file, type = 'banner') => {
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a valid image file (JPEG, PNG, GIF, or WebP)');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image size should be less than 10MB');
      return;
    }

    const setUploadingState = type === 'banner' ? setUploading : setUploadingLogo;
    setUploadingState(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/upload/image`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });

      const imageUrl = `${API_URL}${response.data.url}`;
      
      if (type === 'banner') {
        handleChange('login_banner_url', imageUrl);
        toast.success('Banner image uploaded successfully!');
      } else {
        handleChange('logo_url', imageUrl);
        toast.success('Logo uploaded successfully!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploadingState(false);
    }
  };

  const handleBannerFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleImageUpload(file, 'banner');
    }
  };

  const handleLogoFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleImageUpload(file, 'logo');
    }
  };

  // Check if user is Super Admin
  const isSuperAdmin = user?.role_name === 'Super Admin';

  if (!isSuperAdmin) {
    return (
      <div className="flex items-center justify-center h-64">
        <Card className="p-8 text-center">
          <Settings className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h2 className="text-lg font-semibold mb-2">Access Restricted</h2>
          <p className="text-muted-foreground">Only Super Admins can access app settings.</p>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">App Settings</h1>
          <p className="text-muted-foreground mt-1">Customize your application appearance</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchSettings} disabled={loading}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-settings-btn">
            {saving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Save Changes
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Login Banner Settings */}
        <Card data-testid="banner-settings-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Image className="h-5 w-5" />
              Login Banner Image
            </CardTitle>
            <CardDescription>
              The background image displayed on the login page
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Toggle between Upload and URL */}
            <div className="flex gap-2 p-1 bg-muted rounded-lg w-fit">
              <Button
                type="button"
                variant={inputMode === 'upload' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setInputMode('upload')}
                className="gap-2"
              >
                <Upload className="h-4 w-4" />
                Upload
              </Button>
              <Button
                type="button"
                variant={inputMode === 'url' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setInputMode('url')}
                className="gap-2"
              >
                <Link className="h-4 w-4" />
                URL
              </Button>
            </div>

            {inputMode === 'upload' ? (
              <div className="space-y-2">
                <Label>Upload Image</Label>
                <input
                  type="file"
                  ref={bannerFileRef}
                  onChange={handleBannerFileChange}
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  className="hidden"
                />
                <div 
                  className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-primary hover:bg-primary/5 transition-colors"
                  onClick={() => bannerFileRef.current?.click()}
                  data-testid="banner-upload-area"
                >
                  {uploading ? (
                    <div className="flex flex-col items-center gap-2">
                      <Loader2 className="h-8 w-8 animate-spin text-primary" />
                      <span className="text-sm text-muted-foreground">Uploading...</span>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2">
                      <Upload className="h-8 w-8 text-muted-foreground" />
                      <span className="text-sm font-medium">Click to upload banner image</span>
                      <span className="text-xs text-muted-foreground">
                        JPEG, PNG, GIF, WebP (max 10MB)
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="banner_url">Image URL</Label>
                <Input
                  id="banner_url"
                  placeholder="https://images.unsplash.com/..."
                  value={settings.login_banner_url}
                  onChange={(e) => handleChange('login_banner_url', e.target.value)}
                  data-testid="banner-url-input"
                />
                <p className="text-xs text-muted-foreground">
                  Tip: Use high-quality images from Unsplash, Pexels, or your own hosted images.
                </p>
              </div>
            )}
            
            {/* Preview */}
            {previewUrl && (
              <div className="space-y-2">
                <Label className="flex items-center gap-1">
                  <Eye className="h-4 w-4" /> Current Preview
                </Label>
                <div 
                  className="h-40 rounded-lg bg-cover bg-center border"
                  style={{ backgroundImage: `url(${previewUrl})` }}
                />
              </div>
            )}
            
            {/* New URL Preview */}
            {settings.login_banner_url && settings.login_banner_url !== previewUrl && (
              <div className="space-y-2">
                <Label className="flex items-center gap-1 text-amber-600">
                  <Eye className="h-4 w-4" /> New Image Preview (unsaved)
                </Label>
                <div 
                  className="h-40 rounded-lg bg-cover bg-center border border-amber-300"
                  style={{ backgroundImage: `url(${settings.login_banner_url})` }}
                />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Branding Settings */}
        <Card data-testid="branding-settings-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Type className="h-5 w-5" />
              Branding
            </CardTitle>
            <CardDescription>
              Customize your app name and tagline
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="app_name">App Name</Label>
              <Input
                id="app_name"
                placeholder="Happy Retreats"
                value={settings.app_name}
                onChange={(e) => handleChange('app_name', e.target.value)}
                data-testid="app-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="app_tagline">Tagline</Label>
              <Textarea
                id="app_tagline"
                placeholder="Centralized inventory management..."
                value={settings.app_tagline}
                onChange={(e) => handleChange('app_tagline', e.target.value)}
                rows={3}
                data-testid="app-tagline-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Logo (Optional)</Label>
              {/* Toggle between Upload and URL for Logo */}
              <div className="flex gap-2 p-1 bg-muted rounded-lg w-fit">
                <Button
                  type="button"
                  variant={logoInputMode === 'upload' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setLogoInputMode('upload')}
                  className="gap-2"
                >
                  <Upload className="h-4 w-4" />
                  Upload
                </Button>
                <Button
                  type="button"
                  variant={logoInputMode === 'url' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setLogoInputMode('url')}
                  className="gap-2"
                >
                  <Link className="h-4 w-4" />
                  URL
                </Button>
              </div>

              {logoInputMode === 'upload' ? (
                <div className="space-y-2">
                  <input
                    type="file"
                    ref={logoFileRef}
                    onChange={handleLogoFileChange}
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    className="hidden"
                  />
                  <div 
                    className="border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:border-primary hover:bg-primary/5 transition-colors"
                    onClick={() => logoFileRef.current?.click()}
                    data-testid="logo-upload-area"
                  >
                    {uploadingLogo ? (
                      <div className="flex items-center justify-center gap-2">
                        <Loader2 className="h-5 w-5 animate-spin text-primary" />
                        <span className="text-sm text-muted-foreground">Uploading...</span>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center gap-2">
                        <Upload className="h-5 w-5 text-muted-foreground" />
                        <span className="text-sm">Click to upload logo</span>
                      </div>
                    )}
                  </div>
                  {settings.logo_url && (
                    <div className="flex items-center gap-2 p-2 bg-muted rounded">
                      <img 
                        src={getBillUrl(settings.logo_url)} 
                        alt="Logo preview" 
                        className="h-8 w-auto object-contain"
                      />
                      <span className="text-xs text-muted-foreground truncate flex-1">
                        {settings.logo_url.split('/').pop()}
                      </span>
                    </div>
                  )}
                </div>
              ) : (
                <Input
                  id="logo_url"
                  placeholder="https://your-domain.com/logo.png"
                  value={settings.logo_url}
                  onChange={(e) => handleChange('logo_url', e.target.value)}
                  data-testid="logo-url-input"
                />
              )}
            </div>
          </CardContent>
        </Card>

        {/* Color Settings */}
        <Card data-testid="color-settings-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Palette className="h-5 w-5" />
              Colors (Coming Soon)
            </CardTitle>
            <CardDescription>
              Customize your app's color theme
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="primary_color">Primary Color</Label>
              <div className="flex gap-2">
                <Input
                  id="primary_color"
                  placeholder="#8B7355"
                  value={settings.primary_color}
                  onChange={(e) => handleChange('primary_color', e.target.value)}
                  className="flex-1"
                  data-testid="primary-color-input"
                />
                <div 
                  className="w-10 h-10 rounded border"
                  style={{ backgroundColor: settings.primary_color || '#8B7355' }}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Color theme customization will be available in a future update.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Quick Tips */}
        <Card>
          <CardHeader>
            <CardTitle>Quick Tips</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-muted-foreground">
            <p>
              <strong>Banner Image:</strong> For best results, use landscape images with a 
              resolution of at least 1920x1080 pixels.
            </p>
            <p>
              <strong>Image Sources:</strong> You can use free images from:
            </p>
            <ul className="list-disc list-inside ml-2 space-y-1">
              <li><a href="https://unsplash.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Unsplash.com</a> - Free high-quality photos</li>
              <li><a href="https://pexels.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Pexels.com</a> - Free stock photos</li>
            </ul>
            <p>
              <strong>How to get an image URL:</strong> Right-click on any image and select 
              "Copy image address" or upload to your own hosting service.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SettingsPage;
